import * as Types from '../gql/_types';

import { VehicleModule } from "./init";
import { mapArrayToStateObject } from "utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'state/_types';
import { subscriptions } from 'state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'state/gql/utils';
import { SetMetaInput } from 'state/Module';

const Module = VehicleModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetVehiclesQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.vehicles.errors = [];
    state.vehicles.loading = true;
    try {
        let { limit, offset, sortKey, sortOrder } = state.vehicles.meta;
        let originalPayload = { limit, offset, order_by: { [sortKey]: sortOrder } };
        let { meta, items } = await effects.gql.queries.GetVehicles({...originalPayload, ...finalPayload})
        state.vehicles.meta.total = meta.aggregate?.count || 0;
        state.vehicles.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
    }
    state.vehicles.loading = false;
}

export const resetState: Action = ({state}) => {
    state.vehicles.errors = []
    state.vehicles.loading = false
    state.vehicles.items = {}
    state.vehicles.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.vehicles.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.vehicles.resetMeta()
    actions.vehicles.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.vehicles.errors = [];
    state.vehicles.loadingMore = true;
    try {
        let { limit, sortKey, sortOrder, page } = state.vehicles.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: { [sortKey]: sortOrder } };
        let { meta, items } = await effects.gql.queries.GetVehicles({...originalPayload, ...finalPayload})
        state.vehicles.meta.total = meta.aggregate?.count || 0;
        state.vehicles.meta.page = newPage;
        state.vehicles.meta.offset = newOffset;
        state.vehicles.items = { ...state.vehicles.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
    }
    state.vehicles.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetVehiclesQueryVariables> = async ({state, effects}, payload) => {
    state.vehicles.errors = [];
    state.vehicles.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetVehicles(payload)
        state.vehicles.meta.total = meta.aggregate?.count || 0;
        state.vehicles.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
    }
    state.vehicles.loading = false;
}

export const getItem: AsyncAction<Types.GetVehicleQueryVariables,Types.GetVehicleQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetVehicle(payload);
        return item;
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetVehicleQueryVariables> = async ({state, actions}, payload) => {
    state.vehicles.loading = true;
    try {
        let item = await actions.vehicles.getItem(payload);
        if (item) {
            state.vehicles.currentItemId = item.id;
            state.vehicles.items[item.id] = item;
        }
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
    }
    state.vehicles.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.vehicles.currentItemId = payload
}

export const setMeta: Action<SetMetaInput<Types.VehicleFragment>> = async ({state}, meta) => {
    state.vehicles.meta = {
        ...state.vehicles.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateVehicleMutationVariables['object'],Types.CreateVehicleMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateVehicleMutationVariables['object']>(payload);
    state.vehicles.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateVehicle({object:finalPayload});
        return item;
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateVehicleMutationVariables,Types.UpdateVehicleMutation['item']> = async ({state, effects}, payload) => {
    state.vehicles.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateVehicle(payload);
        return item;
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteVehicleMutationVariables,Types.DeleteVehicleMutation['item']> = async ({state, effects}, payload) => {
    state.vehicles.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteVehicle(payload);
        return item;
    } catch (e: any) {
        state.vehicles.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetVehiclesMetaSubscriptionVariables> = async ({state, actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetVehiclesMetaSubscription, Types.SyncGetVehiclesMetaSubscriptionVariables>(
        'syncGetVehiclesMeta', 
        subscriptions.SyncGetVehiclesMeta, 
        { where }, 
        actions.vehicles.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetVehiclesMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetVehiclesMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.vehicles.errors = [ ...state.vehicles.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.vehicles.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetVehiclesSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, sortKey, sortOrder } = state.vehicles.meta;
    let originalPayload = { limit, offset, order_by: { [sortKey]: sortOrder } };
    state.vehicles.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetVehiclesSubscription, Types.SyncGetVehiclesSubscriptionVariables>(
        'syncGetVehicles', 
        subscriptions.SyncGetVehicles, 
        {...originalPayload, ...payload}, 
        actions.vehicles.onItemsData,
        () => { state.vehicles.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetVehicles');
    state.vehicles.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetVehiclesSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.vehicles.errors = [ ...state.vehicles.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.vehicles.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetVehiclesSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.vehicles.syncGetItems(finalPayload);
    actions.vehicles.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.vehicles.unsubItemsData();
    actions.vehicles.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetVehicleSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.vehicles.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetVehicleSubscription, Types.SyncGetVehicleSubscriptionVariables>(
        'syncGetVehicle', 
        subscriptions.SyncGetVehicle, 
        payload, 
        actions.vehicles.onItemData,
        () => { state.vehicles.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetVehicle');
    state.vehicles.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetVehicleSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.vehicles.errors = [ ...state.vehicles.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.vehicles.currentItemId = payload.data.item.id;
        state.vehicles.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetVehicleSubscriptionVariables> = async ({actions}, payload) => {
    actions.vehicles.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.vehicles.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};